import { useEffect, useState } from 'react';
import { centsToPesos, PAYMENT_METHODS, EXPENSE_CATEGORIES } from '@cannery/shared';
import { useAuth } from '../../auth/AuthContext';
import { api, ApiError } from '../../lib/api';
import { Badge, Button, Card, ErrorBanner, Field, Modal, Pager, Spinner, inputCls } from '../../components/ui';
import { Icon } from '../../components/Icon';

/**
 * Phase 2E — Expenses.
 *
 * Operating expenses live in their OWN ledger (EXP-LH1-000001…), fully
 * separate from Purchasing: recording a purchase never creates an expense
 * and vice versa. Branch scope, category/search/date filters, create +
 * correction (the number stays immutable server-side).
 */

export interface ExpenseDto {
  id: string;
  number: string;
  branch: { id: string; code: string };
  expenseDate: string;
  category: string;
  description: string;
  amountCents: number;
  paymentMethod: string | null;
  payee: string | null;
  referenceNumber: string | null;
  notes: string | null;
  createdBy: { name: string } | null;
  createdAt: string;
  updatedAt: string;
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' });
}

/** Page size the list asks the server for (the server total drives the count). */
const PAGE_SIZE = 50;

/**
 * Calendar-date display: expenseDate is stored as UTC midnight of the
 * entered date, so it must be formatted WITH time-zone:UTC — otherwise
 * browsers west of UTC shift it back a day (Oct 1 would render Sep 30).
 */
function formatDateOnly(iso: string): string {
  return new Date(iso).toLocaleDateString('en-PH', { dateStyle: 'medium', timeZone: 'UTC' });
}

/** Today as the USER's local calendar date (not UTC - same evening/morning). */
function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

interface ExpenseFormState {
  branchId: string;
  expenseDate: string;
  category: string;
  description: string;
  amount: string; // pesos in the input, converted to centavos
  paymentMethod: string;
  payee: string;
  referenceNumber: string;
  notes: string;
}

function emptyExpenseForm(branchId: string): ExpenseFormState {
  return {
    branchId,
    expenseDate: todayIso(),
    category: EXPENSE_CATEGORIES[0],
    description: '',
    amount: '',
    paymentMethod: '',
    payee: '',
    referenceNumber: '',
    notes: '',
  };
}

function ExpenseFormModal({
  expense,
  defaultBranchId,
  branches,
  onClose,
  onSaved,
}: {
  expense: ExpenseDto | null;
  defaultBranchId: string;
  branches: { id: string; code: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = expense !== null;
  const [form, setForm] = useState<ExpenseFormState>(
    expense
      ? {
          branchId: expense.branch.id,
          expenseDate: expense.expenseDate.slice(0, 10),
          category: expense.category,
          description: expense.description,
          amount: (expense.amountCents / 100).toFixed(2),
          paymentMethod: expense.paymentMethod ?? '',
          payee: expense.payee ?? '',
          referenceNumber: expense.referenceNumber ?? '',
          notes: expense.notes ?? '',
        }
      : emptyExpenseForm(defaultBranchId),
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const set = <K extends keyof ExpenseFormState>(key: K, value: ExpenseFormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const submit = async () => {
    setError(null);
    const amountCents = Math.round(Number(form.amount) * 100);
    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      setError('Amount must be greater than zero.');
      return;
    }
    if (editing && amountCents > 1_000_000_00) {
      setError('Amount is too large.');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/expenses/${expense.id}`, {
          expenseDate: form.expenseDate,
          category: form.category.trim(),
          description: form.description.trim(),
          amountCents,
          paymentMethod: form.paymentMethod || undefined,
          payee: form.payee.trim() || undefined,
          referenceNumber: form.referenceNumber.trim() || undefined,
          notes: form.notes.trim() || undefined,
        });
      } else {
        await api.post('/expenses', {
          branchId: form.branchId,
          expenseDate: form.expenseDate,
          category: form.category.trim(),
          description: form.description.trim(),
          amountCents,
          paymentMethod: form.paymentMethod || undefined,
          payee: form.payee.trim() || undefined,
          referenceNumber: form.referenceNumber.trim() || undefined,
          notes: form.notes.trim() || undefined,
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to save the expense.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={editing ? `Edit ${expense.number}` : 'Record expense'}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={saving || !form.description.trim()}>
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Record expense'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <ErrorBanner message={error} />}
        {!editing && (
          <Field label="Branch" htmlFor="exp-branch">
            <select
              id="exp-branch"
              className={inputCls}
              value={form.branchId}
              onChange={(e) => set('branchId', e.target.value)}
            >
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.code} — {b.name}
                </option>
              ))}
            </select>
          </Field>
        )}
        <div className="grid grid-cols-2 gap-4">
          <Field label="Date" htmlFor="exp-date">
            <input
              id="exp-date"
              type="date"
              className={inputCls}
              value={form.expenseDate}
              onChange={(e) => set('expenseDate', e.target.value)}
            />
          </Field>
          <Field label="Category" htmlFor="exp-category" hint="Suggestions below; any category works">
            <input
              id="exp-category"
              className={inputCls}
              list="expense-categories"
              value={form.category}
              onChange={(e) => set('category', e.target.value)}
            />
            <datalist id="expense-categories">
              {EXPENSE_CATEGORIES.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </Field>
        </div>
        <Field label="Description" htmlFor="exp-desc">
          <input
            id="exp-desc"
            className={inputCls}
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            placeholder="e.g. September electricity bill"
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Amount (₱)" htmlFor="exp-amount">
            <input
              id="exp-amount"
              type="number"
              min={0}
              step="0.01"
              className={inputCls}
              value={form.amount}
              onChange={(e) => set('amount', e.target.value)}
              placeholder="0.00"
            />
          </Field>
          <Field label="Payment method" htmlFor="exp-pay">
            <select
              id="exp-pay"
              className={inputCls}
              value={form.paymentMethod}
              onChange={(e) => set('paymentMethod', e.target.value)}
            >
              <option value="">—</option>
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Supplier / payee (optional)" htmlFor="exp-payee">
            <input
              id="exp-payee"
              className={inputCls}
              value={form.payee}
              onChange={(e) => set('payee', e.target.value)}
            />
          </Field>
          <Field label="Reference / receipt no. (optional)" htmlFor="exp-ref">
            <input
              id="exp-ref"
              className={inputCls}
              value={form.referenceNumber}
              onChange={(e) => set('referenceNumber', e.target.value)}
            />
          </Field>
        </div>
        <Field label="Notes (optional)" htmlFor="exp-notes">
          <textarea
            id="exp-notes"
            className={`${inputCls} h-16 py-2`}
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}

/* ---------------- detail ---------------- */

function DetailModal({
  expense,
  onClose,
  onEdit,
}: {
  expense: ExpenseDto;
  onClose: () => void;
  onEdit: () => void;
}) {
  return (
    <Modal
      title={expense.number}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button onClick={onEdit}>Edit</Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="indigo">{expense.branch.code}</Badge>
          <Badge tone="amber">{expense.category}</Badge>
          <span className="text-sm text-slate-500">
            {formatDateOnly(expense.expenseDate)}
          </span>
        </div>
        <p className="text-2xl font-bold text-slate-900">{centsToPesos(expense.amountCents)}</p>
        <p className="text-sm font-medium text-slate-800">{expense.description}</p>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <div>
            <dt className="text-slate-500">Payment</dt>
            <dd className="font-medium text-slate-800">{expense.paymentMethod ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Payee</dt>
            <dd className="font-medium text-slate-800">{expense.payee ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Reference</dt>
            <dd className="font-medium text-slate-800">{expense.referenceNumber ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Recorded by</dt>
            <dd className="font-medium text-slate-800">{expense.createdBy?.name ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Created</dt>
            <dd className="font-medium text-slate-800">{formatWhen(expense.createdAt)}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Last updated</dt>
            <dd className="font-medium text-slate-800">{formatWhen(expense.updatedAt)}</dd>
          </div>
        </dl>
        {expense.notes && <p className="text-sm text-slate-600">{expense.notes}</p>}
      </div>
    </Modal>
  );
}

/* ---------------- main page ---------------- */

export function ExpensesPage() {
  const { branchFilter, branches, user } = useAuth();
  const [items, setItems] = useState<ExpenseDto[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ExpenseDto | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  // A new filter means a new result set: always start from the first page.
  useEffect(() => {
    setOffset(0);
  }, [branchFilter, query, category, dateFrom, dateTo]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    if (query) params.set('q', query);
    if (category) params.set('category', category);
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(offset));

    void api
      .get<{ total: number; items: ExpenseDto[]; categories: string[] }>(`/expenses?${params.toString()}`)
      .then((res) => {
        if (!cancelled) {
          setItems(res.items);
          setTotal(res.total);
          setCategories(res.categories);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Failed to load expenses.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [branchFilter, query, category, dateFrom, dateTo, offset, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);
  const scopeLabel = branchFilter === 'ALL' ? 'All branches' : `Branch ${branchFilter}`;
  const detail = items.find((e) => e.id === detailId) ?? null;

  const createBranchId =
    branchFilter !== 'ALL'
      ? (branches.find((b) => b.code === branchFilter)?.id ?? '')
      : (user?.branchId ?? branches[0]?.id ?? '');

  // Sum of the rows currently loaded (the server exposes no filtered amount
  // sum, so this is deliberately labelled as a shown-rows subtotal).
  const shownTotalCents = items.reduce((sum, e) => sum + e.amountCents, 0);
  /** True when every matching row is on this page (no other pages exist). */
  const allRowsLoaded = items.length === total;

  return (
    <section className="space-y-4" aria-label="Expenses">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Expenses</h1>
          <p className="mt-1 text-sm text-slate-500">
            {scopeLabel} · operating expenses tracked separately from purchases
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={refresh}>
            <Icon name="refresh" className="h-5 w-5" />
            Refresh
          </Button>
          <Button
            onClick={() => {
              if (!createBranchId) {
                setError('Select a branch (LH1 or LH2) before recording an expense.');
                return;
              }
              setCreating(true);
            }}
          >
            Record expense
          </Button>
        </div>
      </div>

      <Card className="flex flex-wrap items-center gap-3 p-4">
        <input
          aria-label="Search expenses"
          className={`${inputCls} h-11 max-w-xs flex-1`}
          placeholder="Search number, description, payee…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          aria-label="Filter by category"
          className={`${inputCls} h-11 w-auto`}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
          From
          <input
            type="date"
            className={`${inputCls} h-11 w-auto`}
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </label>
        <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
          To
          <input
            type="date"
            className={`${inputCls} h-11 w-auto`}
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </label>
        <span className="ml-auto text-sm text-slate-500">
          {allRowsLoaded ? 'Total' : 'Shown-rows subtotal'}{' '}
          <span className="font-semibold text-slate-800">{centsToPesos(shownTotalCents)}</span>
        </span>
      </Card>

      {error && <ErrorBanner message={error} />}

      <Card className="overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-10">
            <Spinner className="h-6 w-6 text-indigo-600" />
          </div>
        ) : total === 0 ? (
          <div className="py-10 text-center">
            <p className="text-sm font-medium text-slate-700">No expenses match.</p>
            <p className="mt-1 text-xs text-slate-500">
              Record one with <span className="font-semibold">Record expense</span>.
            </p>
          </div>
        ) : (
          <>
            <table className="min-w-full text-sm">
              <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Number</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Branch</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((e) => (
                <tr key={e.id}>
                  <td className="px-4 py-3 font-semibold text-slate-800">{e.number}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {formatDateOnly(e.expenseDate)}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone="amber">{e.category}</Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{e.description}</td>
                  <td className="px-4 py-3">
                    <Badge tone="indigo">{e.branch.code}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-800">
                    {centsToPesos(e.amountCents)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setDetailId(e.id)}>
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
            <Pager
              total={total}
              offset={offset}
              loaded={items.length}
              onPrev={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              onNext={() => setOffset(offset + PAGE_SIZE)}
            />
          </>
        )}
      </Card>

      {(creating || editing) && (
        <ExpenseFormModal
          expense={editing}
          defaultBranchId={createBranchId}
          branches={branches}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={refresh}
        />
      )}
      {detail && !editing && (
        <DetailModal
          expense={detail}
          onClose={() => setDetailId(null)}
          onEdit={() => {
            setEditing(detail);
            setDetailId(null);
          }}
        />
      )}
    </section>
  );
}
